import { useState, type KeyboardEvent, type ReactElement } from 'react';
import { Link, useParams } from '@tanstack/react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import {
  WEEKDAY_NAMES,
  describeCadence,
  habitStreak,
  longestStreak,
  streakUnit,
} from '@selflevel/domain';
import { Marca } from '@selflevel/ui';
import { db } from '../store/db';
import { useAreaMap, useSettings, useToday } from '../store/hooks';
import { setHabitStatus } from '../store/mutations';
import { zonaDelDispositivo } from '../store/session';
import { Card, Empty, fechaLarga, num } from '../ui/primitivas';
import { IconAtras } from '../ui/iconos';
import { EditorHabito } from './editor-habito';
import {
  estadoLegible,
  origenLegible,
  semanasHasta,
  type CeldaCalendario,
} from './habito-calendario';

/**
 * DETALLE DE UN HÁBITO. Lo que el usuario viene a ver aquí es una pregunta:
 * «¿esto lo estoy haciendo de verdad?». Así que arriba va la evidencia —racha
 * actual, mejor racha, días hechos— y debajo el calendario, donde se pueden
 * rellenar días pasados.
 *
 * Se puede marcar hacia atrás porque la vida pasa: olvidarse de abrir la app no
 * es lo mismo que no haber hecho el hábito, y una app que no deja corregir
 * convierte su propio registro en mentira.
 */
export function DetalleHabito(): ReactElement {
  const { habitId } = useParams({ from: '/habito/$habitId' });
  const today = useToday();
  const settings = useSettings();
  const areas = useAreaMap();
  const [editor, setEditor] = useState(false);

  const habit = useLiveQuery(() => db.habits.get(habitId), [habitId]);
  const entries = useLiveQuery(
    async () =>
      (await db.habitEntries.where('habitId').equals(habitId).toArray()).filter(
        (e) => !e.deletedAt,
      ),
    [habitId],
  );

  if (!habit) return <main className="screen" />;

  const dias = (entries ?? []).map((e) => ({ localDate: e.localDate, status: e.status, tz: e.tz }));
  const racha = habitStreak(dias, habit.cadence, today);
  const mejor = longestStreak(dias);
  const hechos = dias.filter((d) => d.status === 'done').length;
  const cuota = habit.cadence.kind === 'timesPerWeek' || habit.cadence.kind === 'timesPerMonth';
  const color = areas.get(habit.areaId)?.colorToken ?? '--dim-1';

  // Un mapa y no un `dias.find()` por celda: con años de historial eran decenas
  // de miles de comparaciones en cada render de esta pantalla.
  const estadoDe = new Map(dias.map((d) => [d.localDate, d.status]));
  const semanas = semanasHasta(today, 12);
  // Lo que marcó otra app, día a día. Se enseña siempre (PRD 14.5).
  const origenDe = new Map(
    (entries ?? []).flatMap((e) => {
      const origen = origenLegible(e.source);
      return origen ? [[e.localDate, origen] as const] : [];
    }),
  );
  const origenes = [...new Set(origenDe.values())];

  return (
    <main className="screen" style={{ ['--area' as string]: `var(${color})` }}>
      <header className="topbar glass-2">
        <Link to="/" className="btn btn--icon btn--bare" aria-label="Volver">
          <IconAtras />
        </Link>
        <h1 className="t-title-3 grow">{habit.name}</h1>
        <button type="button" className="btn btn--sm btn--ghost" onClick={() => setEditor(true)}>
          Editar
        </button>
      </header>

      <section className="row">
        <span className="habit__disc" style={{ width: 56, height: 56 }}>
          <Marca id={habit.marca} size={28} polarity="area" />
        </span>
        <div className="grow">
          <p className="t-display tnum">{racha}</p>
          {/* En los de cuota la racha cuenta semanas o meses cumplidos, no días. */}
          <p className="t-sm t-dim">
            {streakUnit(habit.cadence, racha)} de racha · {describeCadence(habit.cadence)}
          </p>
          {habit.description ? <p className="t-sm">{habit.description}</p> : null}
        </div>
      </section>

      {/* `stretch`: con una fecha de dos líneas la tarjeta de la izquierda crecía
          y la fila centraba a la otra, que quedaba desplazada hacia abajo. */}
      <div className="row" style={{ gap: 'var(--sp-3)', alignItems: 'stretch' }}>
        <Card className="grow">
          {/* `longestStreak` cuenta días hechos seguidos. En un hábito de cuota eso no
              es su racha —que va por semanas o meses—, así que se dice lo que es. */}
          <p className="t-caption t-dim">{cuota ? 'Más días seguidos' : 'Mejor racha'}</p>
          <p className="t-title-1 tnum">{mejor.length}</p>
          {mejor.from ? (
            <p className="t-sm t-dim">
              {fechaLarga(mejor.from)} – {fechaLarga(mejor.to ?? mejor.from)}
            </p>
          ) : null}
        </Card>
        <Card className="grow">
          <p className="t-caption t-dim">Veces hecho</p>
          <p className="t-title-1 tnum">{num(hechos)}</p>
          <p className="t-sm t-dim">desde el principio</p>
        </Card>
      </div>

      <Card title="Las últimas doce semanas">
        <Calendario
          semanas={semanas}
          today={today}
          estadoDe={estadoDe}
          origenDe={origenDe}
          onMarcar={(localDate, estado) =>
            void setHabitStatus({
              habitId: habit.id,
              localDate,
              status: estado === 'done' ? 'pending' : 'done',
              // La zona en la que está el dispositivo, no la del alta.
              tz: zonaDelDispositivo(settings.tz),
            })
          }
        />
        <p className="t-sm t-dim" style={{ marginTop: 'var(--sp-3)' }}>
          Toca un día para rellenarlo. Olvidarse de abrir la app no es lo mismo que no haberlo
          hecho.
        </p>
        {origenes.length > 0 ? (
          <p className="t-sm t-dim cal__leyenda">
            <span className="cal__punto" aria-hidden="true" /> Marcado desde {origenes.join(', ')}.
            Tócalo para cambiarlo, como cualquier otro día.
          </p>
        ) : null}
      </Card>

      {hechos === 0 ? <Empty title="Todavía sin registros">Empieza hoy.</Empty> : null}

      <EditorHabito open={editor} habit={habit} onClose={() => setEditor(false)} />
    </main>
  );
}

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * EL CALENDARIO. Antes eran 84 botones de 11 × 11 px que CAMBIAN DATOS —pulsar
 * uno marca o desmarca ese día—: en un móvil, acertar al día que querías era
 * cuestión de suerte. Y eran 84 paradas de tabulador seguidas, y el lector de
 * pantalla decía «2 de julio de 2026: done».
 *
 * Ahora es un calendario: siete columnas con su cabecera, una fila por semana,
 * y cada día ocupa lo que le toca del ancho —unos 40 px en un móvil, casi
 * cuatro veces el lado de antes—. Con su número dentro, que es lo que dice qué
 * día es sin tener que contar.
 *
 * LOS ACABADOS DEL §14, al pie de la letra: un día sin hacer es un pozo
 * HUNDIDO —algo que se rellena—, y uno hecho es una pieza ACUÑADA en el color
 * del área —algo que se ha ganado—. El de hoy lleva un aro.
 *
 * UNA SOLA PARADA DE TABULADOR. La rejilla entra con el tabulador una vez y se
 * recorre con las flechas: izquierda y derecha un día, arriba y abajo una
 * semana, Inicio y Fin los extremos. Es el patrón de rejilla del WAI-ARIA, y es
 * la diferencia entre llegar al día que buscas en tres pulsaciones o en
 * ochenta.
 * ═══════════════════════════════════════════════════════════════════════════
 */
function Calendario({
  semanas,
  today,
  estadoDe,
  origenDe,
  onMarcar,
}: {
  semanas: readonly (readonly CeldaCalendario[])[];
  today: string;
  estadoDe: ReadonlyMap<string, string>;
  origenDe: ReadonlyMap<string, string>;
  onMarcar: (localDate: string, estado: string | undefined) => void;
}): ReactElement {
  const dias = semanas.flat();
  const marcables = dias.filter((d) => !d.futuro).map((d) => d.localDate);
  const [activo, setActivo] = useState(today);

  const mover = (event: KeyboardEvent<HTMLDivElement>) => {
    const i = marcables.indexOf(activo);
    if (i < 0) return;
    const salto: Record<string, number> = {
      ArrowLeft: -1,
      ArrowRight: 1,
      ArrowUp: -7,
      ArrowDown: 7,
      Home: -i,
      End: marcables.length - 1 - i,
    };
    const paso = salto[event.key];
    if (paso === undefined) return;
    event.preventDefault();
    const destino = marcables[Math.min(marcables.length - 1, Math.max(0, i + paso))];
    if (!destino) return;
    setActivo(destino);
    event.currentTarget.querySelector<HTMLButtonElement>(`[data-dia="${destino}"]`)?.focus();
  };

  return (
    <div className="cal">
      <div className="cal__cabecera" aria-hidden="true">
        {WEEKDAY_NAMES.map((d) => (
          <span key={d}>{d}</span>
        ))}
      </div>
      <div
        className="cal__rejilla"
        role="group"
        aria-label="Últimas doce semanas. Flechas para moverte por los días."
        onKeyDown={mover}
      >
        {dias.map((dia) =>
          dia.futuro ? (
            <span
              key={dia.localDate}
              className="cal__celda cal__celda--futuro"
              aria-hidden="true"
            />
          ) : (
            <button
              key={dia.localDate}
              type="button"
              className="cal__celda"
              data-dia={dia.localDate}
              data-estado={estadoDe.get(dia.localDate) ?? 'vacio'}
              data-hoy={dia.localDate === today}
              tabIndex={dia.localDate === activo ? 0 : -1}
              aria-pressed={estadoDe.get(dia.localDate) === 'done'}
              data-externo={origenDe.has(dia.localDate) || undefined}
              aria-label={`${fechaLarga(dia.localDate, true)}: ${estadoLegible(estadoDe.get(dia.localDate))}${
                origenDe.has(dia.localDate) ? `, desde ${origenDe.get(dia.localDate)}` : ''
              }`}
              onFocus={() => setActivo(dia.localDate)}
              onClick={() => onMarcar(dia.localDate, estadoDe.get(dia.localDate))}
            >
              {Number(dia.localDate.slice(8, 10))}
              {dia.mes ? <span className="cal__mes">{dia.mes}</span> : null}
            </button>
          ),
        )}
      </div>
    </div>
  );
}
